/**
 * LocaleContext - Multi-currency and locale management
 * 
 * Provides:
 * - Auto-detection of user's country via IP geolocation
 * - Locale-based routing (e.g., /uk-en/hotel-quality-pillow)
 * - Currency formatting with locale-specific conventions (Intl.NumberFormat)
 * - Price lookup from DB-backed locale pricing
 * 
 * Formatting conventions:
 * - UK (GBP): £59.00 (symbol before, dot decimal, 2 decimals)
 * - AU (AUD): AUD $99.00 (currency code prefix to disambiguate from USD)
 * - EU (EUR): 69,00 € (symbol after, comma decimal, 2 decimals)
 * - US (USD): $69.90 (symbol before, dot decimal, 2 decimals)
 * 
 * Non-US dollar currencies (AUD, CAD, NZD, SGD, etc.) are automatically
 * prefixed with their ISO currency code to avoid confusion with USD.
 */

import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { trpc } from '@/lib/trpc';

// Locale-to-BCP47 mapping for Intl.NumberFormat
const LOCALE_FORMAT_MAP: Record<string, string> = {
  'US': 'en-US',
  'GB': 'en-GB',
  'AU': 'en-AU',
  'EU': 'de-DE',  // Most common EU format: comma decimal, symbol after
  'FR': 'fr-FR',
  'DE': 'de-DE',
  'IT': 'it-IT',
  'ES': 'es-ES',
  'NL': 'nl-NL',
  'BE': 'fr-BE',
  'AT': 'de-AT',
  'IE': 'en-IE',
  'PT': 'pt-PT',
  'FI': 'fi-FI',
  'CA': 'en-CA',
  'NZ': 'en-NZ',
  'JP': 'ja-JP',
  'SG': 'en-SG',
  'HK': 'en-HK',
  'AE': 'ar-AE',
  'SA': 'ar-SA',
};

// Default locale (US/USD) - used when no locale is detected
const DEFAULT_LOCALE = {
  id: 0,
  countryCode: 'US',
  languageCode: 'en',
  name: 'United States',
  currencyCode: 'USD',
  currencySymbol: '$',
  symbolPosition: 'before' as const,
  pathPrefix: '',
  isActive: 1,
  sortOrder: 0,
};

export interface LocaleInfo {
  id: number;
  countryCode: string;
  languageCode: string;
  name: string;
  currencyCode: string;
  currencySymbol: string;
  symbolPosition: 'before' | 'after';
  pathPrefix: string;
  isActive: number | null;
  sortOrder: number | null;
}

interface PricingMap {
  [priceKey: string]: number;
}

interface LocaleContextType {
  /** Current active locale */
  locale: LocaleInfo;
  /** Whether we're still detecting the locale */
  isLoading: boolean;
  /** Whether the user is on a non-default locale */
  isInternational: boolean;
  /** All active locales for locale switcher */
  availableLocales: LocaleInfo[];
  /** Pricing map for current locale (priceKey -> price value) */
  pricing: PricingMap;
  /** Whether pricing has been loaded */
  pricingLoaded: boolean;
  /** Format a price with the current locale's currency conventions */
  formatPrice: (amount: number) => string;
  /** Format a price without decimals (for whole numbers like £59) */
  formatPriceCompact: (amount: number) => string;
  /** Get a locale-specific price by key, falling back to USD amount */
  getPrice: (priceKey: string, fallbackUsd: number) => number;
  /** Switch to a different locale */
  switchLocale: (pathPrefix: string) => void;
  /** Get the locale-prefixed path for a given route */
  getLocalePath: (path: string) => string;
}

const LocaleContext = createContext<LocaleContextType | null>(null);

const LOCALE_STORAGE_KEY = 'fluffco_locale';

interface StoredLocale {
  pathPrefix: string;
  timestamp: number;
}

function getStoredLocale(): string | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (!stored) return null;
    const parsed: StoredLocale = JSON.parse(stored);
    // Cache for 24 hours
    if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
      return parsed.pathPrefix;
    }
    localStorage.removeItem(LOCALE_STORAGE_KEY);
    return null;
  } catch {
    return null;
  }
}

function setStoredLocale(pathPrefix: string): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify({
      pathPrefix,
      timestamp: Date.now(),
    }));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Extract locale prefix from current URL path
 * e.g., /uk-en/hotel-quality-pillow -> 'uk-en'
 */
function getLocaleFromPath(): string | null {
  const path = window.location.pathname;
  const match = path.match(/^\/([a-z]{2}-[a-z]{2})(\/|$)/);
  return match ? match[1] : null;
}

/**
 * Disambiguate non-US dollar currencies by prepending the currency code.
 * Intl.NumberFormat renders AUD, CAD, NZD, SGD all as plain "$" which is
 * indistinguishable from USD. For any non-US locale whose formatted output
 * starts with "$", we prepend the ISO currency code (e.g., "AUD $99.00").
 * Currencies that already have a distinct symbol (£, €, HK$) are untouched.
 */
function disambiguateDollar(formatted: string, currencyCode: string, countryCode: string): string {
  if (countryCode === 'US' || currencyCode === 'USD') return formatted;
  if (formatted.startsWith('$')) {
    return `${currencyCode} ${formatted}`;
  }
  return formatted;
}

/**
 * Create a locale-aware price formatter using Intl.NumberFormat
 * This respects each country's conventions for:
 * - Symbol placement (before/after)
 * - Decimal separator (dot vs comma)
 * - Thousands separator
 * - Number of decimal places
 * 
 * Non-US dollar currencies (AUD, CAD, NZD, etc.) are automatically
 * disambiguated with a currency code prefix: "AUD $99.00"
 */
function createPriceFormatter(countryCode: string, currencyCode: string) {
  const bcp47 = LOCALE_FORMAT_MAP[countryCode] || 'en-US';
  
  // Full formatter with decimals (e.g., £59.00, 69,00 €, AUD $99.00)
  const fullFormatter = new Intl.NumberFormat(bcp47, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Compact formatter - no decimals for whole numbers (e.g., £59, 69 €, AUD $99)
  const compactFormatter = new Intl.NumberFormat(bcp47, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return {
    format: (amount: number): string => {
      return disambiguateDollar(fullFormatter.format(amount), currencyCode, countryCode);
    },
    formatCompact: (amount: number): string => {
      // If the amount is a whole number, show without decimals
      if (Number.isInteger(amount)) {
        return disambiguateDollar(compactFormatter.format(amount), currencyCode, countryCode);
      }
      return disambiguateDollar(fullFormatter.format(amount), currencyCode, countryCode);
    },
  };
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [currentLocale, setCurrentLocale] = useState<LocaleInfo>(DEFAULT_LOCALE);
  const [isLoading, setIsLoading] = useState(true);
  const [pricing, setPricing] = useState<PricingMap>({});
  const [pricingLoaded, setPricingLoaded] = useState(false);

  // Fetch active locales
  const { data: activeLocales } = trpc.locales.getActive.useQuery();

  // Detect locale from URL path first, then from geolocation
  useEffect(() => {
    if (!activeLocales) return;

    // 1. Check URL path for locale prefix
    const pathLocale = getLocaleFromPath();
    if (pathLocale) {
      const found = activeLocales.find(l => l.pathPrefix === pathLocale);
      if (found) {
        setCurrentLocale(found as LocaleInfo);
        setStoredLocale(found.pathPrefix);
        setIsLoading(false);
        return;
      }
    }

    // 2. Check localStorage for previously detected locale
    // IMPORTANT: Only use stored locale if the user is NOT on the base URL.
    // If the user navigated to "/" (no locale prefix), they expect US locale.
    // The stored locale should only persist within locale-prefixed routes.
    const storedPrefix = getStoredLocale();
    const isOnBaseUrl = !pathLocale; // No locale prefix in URL
    if (storedPrefix && !isOnBaseUrl) {
      const found = activeLocales.find(l => l.pathPrefix === storedPrefix);
      if (found) {
        setCurrentLocale(found as LocaleInfo);
        setIsLoading(false);
        return;
      }
    }
    // If user is on base URL, clear any stale stored locale
    if (isOnBaseUrl && storedPrefix) {
      localStorage.removeItem(LOCALE_STORAGE_KEY);
    }

    // 3. Auto-detect from geolocation (use existing cached geo data)
    const geoData = localStorage.getItem('fluffco_geolocation');
    if (geoData) {
      try {
        const parsed = JSON.parse(geoData);
        const countryCode = parsed.data?.countryCode;
        if (countryCode) {
          const found = activeLocales.find(l => l.countryCode === countryCode.toUpperCase());
          if (found) {
            setCurrentLocale(found as LocaleInfo);
            setStoredLocale(found.pathPrefix);
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // Ignore parse errors
      }
    }

    // 4. Default to US
    setCurrentLocale(DEFAULT_LOCALE);
    setIsLoading(false);
  }, [activeLocales]);

  // Fetch pricing for current locale
  const { data: localePricingData } = trpc.localePricing.getByLocale.useQuery(
    { localeId: currentLocale.id },
    { enabled: currentLocale.id > 0 }
  );

  useEffect(() => {
    if (localePricingData) {
      const map: PricingMap = {};
      localePricingData.forEach(p => {
        map[p.priceKey] = parseFloat(p.price);
      });
      setPricing(map);
      setPricingLoaded(true);
    } else if (currentLocale.id === 0) {
      // US locale - no DB pricing needed
      setPricing({});
      setPricingLoaded(true);
    }
  }, [localePricingData, currentLocale.id]);

  // Create locale-aware formatters
  const formatter = useMemo(() => {
    return createPriceFormatter(currentLocale.countryCode, currentLocale.currencyCode);
  }, [currentLocale.countryCode, currentLocale.currencyCode]);

  const formatPrice = useMemo(() => {
    return (amount: number): string => formatter.format(amount);
  }, [formatter]);

  const formatPriceCompact = useMemo(() => {
    return (amount: number): string => formatter.formatCompact(amount);
  }, [formatter]);

  const getPrice = useMemo(() => {
    return (priceKey: string, fallbackUsd: number): number => {
      if (currentLocale.id === 0) return fallbackUsd; // US locale
      return pricing[priceKey] ?? fallbackUsd; // Fallback to USD if no locale price
    };
  }, [currentLocale.id, pricing]);

  const switchLocale = (pathPrefix: string) => {
    if (!pathPrefix) {
      // Switch to US (default)
      setCurrentLocale(DEFAULT_LOCALE);
      setStoredLocale('');
      // Remove locale prefix from URL
      const currentPath = window.location.pathname;
      const match = currentPath.match(/^\/[a-z]{2}-[a-z]{2}(\/.*)?$/);
      if (match) {
        window.location.href = match[1] || '/';
      }
      return;
    }

    const found = activeLocales?.find(l => l.pathPrefix === pathPrefix);
    if (found) {
      setCurrentLocale(found as LocaleInfo);
      setStoredLocale(found.pathPrefix);
      // Redirect to locale-prefixed URL
      const currentPath = window.location.pathname;
      const cleanPath = currentPath.replace(/^\/[a-z]{2}-[a-z]{2}/, '') || '/';
      window.location.href = `/${pathPrefix}${cleanPath}`;
    }
  };

  const getLocalePath = (path: string): string => {
    if (!currentLocale.pathPrefix) return path;
    return `/${currentLocale.pathPrefix}${path.startsWith('/') ? path : '/' + path}`;
  };

  const isInternational = currentLocale.id !== 0;
  const availableLocales = useMemo(() => {
    const list: LocaleInfo[] = [DEFAULT_LOCALE];
    if (activeLocales) {
      list.push(...(activeLocales as LocaleInfo[]));
    }
    return list;
  }, [activeLocales]);

  return (
    <LocaleContext.Provider value={{
      locale: currentLocale,
      isLoading,
      isInternational,
      availableLocales,
      pricing,
      pricingLoaded,
      formatPrice,
      formatPriceCompact,
      getPrice,
      switchLocale,
      getLocalePath,
    }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextType {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    // Return a default context if not wrapped in provider (backwards compatible)
    return {
      locale: DEFAULT_LOCALE,
      isLoading: false,
      isInternational: false,
      availableLocales: [DEFAULT_LOCALE],
      pricing: {},
      pricingLoaded: true,
      formatPrice: (amount: number) => `$${amount.toFixed(2)}`,
      formatPriceCompact: (amount: number) => Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`,
      getPrice: (_key: string, fallback: number) => fallback,
      switchLocale: () => {},
      getLocalePath: (path: string) => path,
    };
  }
  return ctx;
}
